import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { LanguageProvider } from "@/hooks/useLanguage";


// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const Index2 = lazy(() => import("./pages/Index2"));
const Index3 = lazy(() => import("./pages/Index3"));
const Manifesto = lazy(() => import("./pages/Manifesto"));
const Apresentacao = lazy(() => import("./pages/Apresentacao"));
const PropositoAmbicao = lazy(() => import("./pages/PropositoAmbicao"));
const PrincipiosOrientadores = lazy(() => import("./pages/PrincipiosOrientadores"));
const OrgaosGestao = lazy(() => import("./pages/listas/OrgaosGestao"));
const OrgaosGestaoV2 = lazy(() => import("./pages/listas/OrgaosGestaoV2"));
const ConselhoEscola = lazy(() => import("./pages/listas/ConselhoEscola"));
const ConselhoCientifico = lazy(() => import("./pages/listas/ConselhoCientifico"));
const ConselhoPedagogico = lazy(() => import("./pages/listas/ConselhoPedagogico"));
const Calendario = lazy(() => import("./pages/Calendario"));
const Documentos = lazy(() => import("./pages/Documentos"));
const Noticias = lazy(() => import("./pages/Noticias"));
const NoticiaDetalhe = lazy(() => import("./pages/NoticiaDetalhe"));
const ContactosV2 = lazy(() => import("./pages/ContactosV2"));
const ContactosV3 = lazy(() => import("./pages/ContactosV3"));
const ContactosV4 = lazy(() => import("./pages/ContactosV4"));
const ContactosV5 = lazy(() => import("./pages/ContactosV5"));
const ParticipaImagem = lazy(() => import("./pages/ParticipaImagem"));
const JuntaTeANos = lazy(() => import("./pages/JuntaTeANos"));
const Blog = lazy(() => import("./pages/Blog"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Eixos - lazy with named exports
const EixoPage = lazy(() => import("./pages/eixos/EixoPage"));
const LazyEixo = ({ eixo }: { eixo: string }) => {
  const mod = import("./pages/eixos/EixoPage");
  const Component = lazy(() => mod.then(m => ({ default: (m as any)[eixo] })));
  return <Suspense fallback={null}><Component /></Suspense>;
};

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={null}>
              <Routes>
                {/* Home */}
                <Route path="/" element={<Index />} />
                <Route path="/homepage2" element={<Index2 />} />
                <Route path="/homepage3" element={<Index3 />} />
                
                {/* Publicações */}
                <Route path="/publicacoes" element={<Manifesto />} />
                <Route path="/manifesto" element={<Manifesto />} />
                <Route path="/apresentacao" element={<Apresentacao />} />
                
                {/* Strategic Axes */}
                <Route path="/ambicao" element={<PropositoAmbicao />} />
                <Route path="/principios-orientadores" element={<PrincipiosOrientadores />} />
                <Route path="/eixo-1-motivar-valorizar-talento" element={<LazyEixo eixo="Eixo1" />} />
                <Route path="/eixo-2-inovacao-transformacao-digital" element={<LazyEixo eixo="Eixo2" />} />
                <Route path="/eixo-3-excelencia-investigacao" element={<LazyEixo eixo="Eixo3" />} />
                <Route path="/eixo-4-excelencia-ensino" element={<LazyEixo eixo="Eixo4" />} />
                <Route path="/eixo-5-impacto-internacional-acreditacoes" element={<LazyEixo eixo="Eixo5" />} />
                <Route path="/eixo-6-bem-estar-impacto-ambiental" element={<LazyEixo eixo="Eixo6" />} />
                <Route path="/eixo-7-bolsas-incentivos" element={<LazyEixo eixo="Eixo7" />} />
                <Route path="/eixo-8-modernizar-instalacoes" element={<LazyEixo eixo="Eixo8" />} />
                <Route path="/eixo-9-ulisboa-parcerias" element={<LazyEixo eixo="Eixo9" />} />
                <Route path="/eixo-10-eventos-cientificos-culturais-desportivos" element={<LazyEixo eixo="Eixo10" />} />
                
                {/* Lists */}
                <Route path="/listas/orgaos-gestao" element={<OrgaosGestao />} />
                <Route path="/listas/orgaos-gestao-v2" element={<OrgaosGestaoV2 />} />
                <Route path="/listas/conselho-escola" element={<ConselhoEscola />} />
                <Route path="/listas/conselho-cientifico" element={<ConselhoCientifico />} />
                <Route path="/listas/conselho-pedagogico" element={<ConselhoPedagogico />} />
                
                {/* Calendar */}
                <Route path="/calendario" element={<Calendario />} />
                
                {/* Documents */}
                <Route path="/documentos" element={<Documentos />} />
                
                {/* News */}
                <Route path="/noticias" element={<Noticias />} />
                <Route path="/noticias/:slug" element={<NoticiaDetalhe />} />
                
                {/* Blog */}
                <Route path="/blog" element={<Blog />} />

                {/* Contacts */}
                <Route path="/participa" element={<ContactosV5 />} />
                <Route path="/contactos-v2" element={<ContactosV2 />} />
                <Route path="/contactos-v3" element={<ContactosV3 />} />
                <Route path="/contactos-v4" element={<ContactosV4 />} />
                <Route path="/participa-imagem" element={<ParticipaImagem />} />
                <Route path="/junta-te-a-nos" element={<JuntaTeANos />} />
                
                {/* 404 - Must be last */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
